import { Button, Input } from '@lomadee/design-system';
import MaskedInput from '@plug/components/masked-input';
import { useCreateOrganizationContext } from '@plug/contexts/CreateOrganizationContext';
import useServiceGetCepDetailsAtViaCep from '@plug/domains/auth/services/useServiceGetCepDetailsAtViaCep';
import { useStates } from '@plug/hooks/use-states';
import { Spacer } from '@plug/ui';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Flex } from 'rebass';
import { TitleForm } from '../../../affiliate-registration/style';
import { OrganizationRegisterAddressDataSchema } from './OrganizationRegistrationAddressData.schema';

const OrganizationRegistrationAddressDataStep: React.FC = () => {
  const { t } = useTranslation();
  const states = useStates();
  const [hasViaCepData, setHasViaCepData] = useState(false);
  const { values: contextValues, setStep, addValues } = useCreateOrganizationContext();
  const { getCepDetailsAtViaCep } = useServiceGetCepDetailsAtViaCep();

  const statesDropdownOptions = states.map(state => ({ value: state.acronym }));

  const handleSubmitFormik = (formikValues: CreateOrganizationFormI): void => {
    addValues(formikValues);
    setStep('INVITE_TEAMMATES');
  };

  const handleBackToOrganizationDataStep = (): void => {
    setStep('ORGANIZATION_DATA');
  };

  const handleSearchCepAtViaCepAndSetContextAndFormik = async (
    cep: string,
    setFormikValues: SetCreateOrganizationFormikValueI,
    formikValues: CreateOrganizationFormI,
  ): Promise<void> => {
    console.log('init handleSearchCepAtViaCepAndSetContextAndFormik');
    const service = await getCepDetailsAtViaCep(cep, toast.error);

    if (service?.valid && service?.data) {
      setHasViaCepData(true);
      addValues(service.data);

      if (setFormikValues) {
        setFormikValues({
          ...formikValues,
          ...service.data,
        });
      }
    }
  };

  const handleCepInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFormikValues: SetCreateOrganizationFormikValueI,
    formikValues: CreateOrganizationFormI,
  ): void => {
    const cep = evt.target.value;
    const cepOnlyNumbers = cep?.replace(/\D/g, '');

    if (cepOnlyNumbers.length !== 8 && hasViaCepData) {
      setHasViaCepData(false);
    }

    if (cepOnlyNumbers.length === 8) {
      handleSearchCepAtViaCepAndSetContextAndFormik(cep, setFormikValues, formikValues);
    }
  };

  const goToCorreiosBuscaCep = (): void => {
    window.open('https://buscacepinter.correios.com.br/app/endereco/index.php', '_blank');
  };

  return (
    <Flex flexDirection="column" sx={{ gap: '32px' }}>
      <Spacer mt="32px" />

      <TitleForm>
        <Trans i18nKey="hubly-dashboard.signup.admin.right-side.fourth-step.title" />
      </TitleForm>

      <Formik
        initialValues={contextValues}
        validationSchema={OrganizationRegisterAddressDataSchema}
        isInitialValid={false}
        onSubmit={handleSubmitFormik}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          handleBlur,
          errors,
          isValid,
          touched,
          setFieldValue,
          setValues: setFormikValues,
        }): JSX.Element => (
          <Flex flexDirection="column" sx={{ gap: '16px' }}>
            <MaskedInput
              mask="99999-999"
              label={t(
                'hubly-dashboard.signup.admin.right-side.fourth-step.form.postal-code.label',
              )}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.fourth-step.form.postal-code.placeholder',
              )}
              name="cep"
              value={values.cep}
              onChange={(evt): void => {
                handleChange(evt);
                handleCepInputChange(evt, setFormikValues, values);
              }}
              onBlur={handleBlur}
              error={touched.cep && !!errors.cep}
              helperText="Não sei meu CEP"
              helperTextOnClick={goToCorreiosBuscaCep}
              fluid
            />

            <Flex sx={{ gap: '16px' }}>
              <Flex flex={2}>
                <Input
                  label={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.address.label',
                  )}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.address.placeholder',
                  )}
                  name="street"
                  value={values.street}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.street && !!errors.street}
                  helperText={touched.street ? errors.street : ''}
                  disabled={!!hasViaCepData}
                  fluid
                />
              </Flex>

              <Flex flex={1}>
                <Input
                  label={t('hubly-dashboard.signup.admin.right-side.fourth-step.form.number.label')}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.number.placeholder',
                  )}
                  name="number"
                  value={values.number}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.number && !!errors.number}
                  helperText={touched.number ? errors.number : ''}
                  fluid
                />
              </Flex>
            </Flex>

            <Flex sx={{ gap: '16px' }}>
              <Flex flex={2}>
                <Input
                  label={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.complement.label',
                  )}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.complement.placeholder',
                  )}
                  name="complement"
                  value={values.complement}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.complement && !!errors.complement}
                  helperText={touched.complement ? errors.complement : ''}
                  fluid
                />
              </Flex>

              <Flex flex={3}>
                <Input
                  label={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.district.label',
                  )}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.district.placeholder',
                  )}
                  name="neighbourhood"
                  value={values.neighbourhood}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.neighbourhood && !!errors.neighbourhood}
                  helperText={touched.neighbourhood ? errors.neighbourhood : ''}
                  fluid
                  disabled={!!hasViaCepData}
                />
              </Flex>
            </Flex>

            <Flex sx={{ gap: '16px' }}>
              <Flex flex={1}>
                <Input
                  onClickDropdownItem={clickedValue => setFieldValue('state', clickedValue)}
                  dropdown={statesDropdownOptions}
                  label={t('hubly-dashboard.signup.admin.right-side.fourth-step.form.state.label')}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.state.placeholder',
                  )}
                  name="state"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.state && !!errors.state}
                  helperText={touched.state ? errors.state : ''}
                  fluid
                  disabled={!!hasViaCepData}
                />
              </Flex>

              <Flex flex={2}>
                <Input
                  label={t('hubly-dashboard.signup.admin.right-side.fourth-step.form.city.label')}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.fourth-step.form.city.placeholder',
                  )}
                  name="city"
                  value={values.city}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.city && !!errors.city}
                  helperText={touched.city ? errors.city : ''}
                  fluid
                  disabled={!!hasViaCepData}
                />
              </Flex>
            </Flex>

            <Button type="submit" disabled={!isValid} onClick={(): void => handleSubmit()}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.confirm.text')}
            </Button>

            <Button hierarchy="secondary" onClick={handleBackToOrganizationDataStep}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.back.text')}
            </Button>
          </Flex>
        )}
      </Formik>
    </Flex>
  );
};

export default OrganizationRegistrationAddressDataStep;
